/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { ExternalStyles, HFlow, isEmpty, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { RadioOption, SimNaoEnum, SimNaoRadioGroupField } from 'components/form'
import { RequiredLabel } from 'components/form/final-form/RequiredLabel/RequiredLabel'
import { ReactNode } from 'react'
import { MetaPath } from 'util/metaPath'

export interface SimNaoRadioListFieldProps<T extends string> {
  name: MetaPath<Record<T, SimNaoEnum>>
  label: ReactNode
  description?: string
  options: RadioOption<T>[]
  required?: boolean
  labelStyle?: ExternalStyles
}

export function SimNaoRadioListField<T extends string>(props: Readonly<SimNaoRadioListFieldProps<T>>) {
  const { name, label, description, options, required, labelStyle } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const hasDescription = !isEmpty(description)

  return (
    <VFlow vSpacing={0.5}>
      {hasDescription &&
        (required ? (
          <RequiredLabel fontWeight='bold' label={description} />
        ) : (
          <Text fontWeight='bold'>{description}</Text>
        ))}
      <Box style={styles.box}>
        <VFlow vSpacing={0} style={styles.list}>
          <div css={styles.header}>
            {required && !hasDescription ? (
              <RequiredLabel fontWeight='bold' label={label} style={labelStyle} />
            ) : (
              <Text fontWeight='bold' style={labelStyle}>
                {label}
              </Text>
            )}
          </div>
          {options.map((option) => (
            <HFlow key={option.value} alignItems='center' justifyContent='space-between' style={styles.listItem}>
              <Text>{option.label}</Text>
              <SimNaoRadioGroupField
                name={name[option.value.toString()]}
                hSpacing={0.5}
                style={{ hFlow: styles.radio }}
              />
            </HFlow>
          ))}
        </VFlow>
      </Box>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  box: css`
    padding: 0;
  `,
  list: css`
    & > :not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.gray.c80};
    }
  `,
  header: css`
    padding: 0.875rem 1rem;
  `,
  listItem: css`
    padding: 0.75rem 1rem;
  `,
  radio: css`
    justify-content: flex-end;
  `,
})
